import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  ShieldCheck,
  User,
  MapPin,
  Camera,
  Edit3,
  Save,
  Plus,
  X,
  UserX,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = [
  "Photography", "Travel", "Music", "Art", "Cooking", "Fitness",
  "Reading", "Hiking", "Movies", "Technology", "Fashion", "Gaming",
  "Yoga", "Dancing", "Sports", "Coffee", "Wine", "Nature",
];

export default function ProfileView({ user, token, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState(user?.bio || "");
  const [location, setLocation] = useState(user?.location || "");
  const [interests, setInterests] = useState(user?.interests || []);
  const [photoUrl, setPhotoUrl] = useState("");
  const [photos, setPhotos] = useState(user?.photos || []);
  const [anonymousMode, setAnonymousMode] = useState(!!user?.anonymous_mode);
  const [saving, setSaving] = useState(false);

  const toggleInterest = (interest) => {
    if (interests.includes(interest)) {
      setInterests(interests.filter((i) => i !== interest));
    } else if (interests.length < 8) {
      setInterests([...interests, interest]);
    }
  };

  const addPhoto = () => {
    if (photoUrl && photos.length < 6) {
      setPhotos([...photos, photoUrl]);
      setPhotoUrl("");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bio, location, interests, photos, anonymous_mode: anonymousMode }),
      });
      const data = await res.json();
      if (res.ok) {
        onUpdate(data.user);
        setEditing(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-6 md:p-10" data-testid="profile-view">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <User className="w-5 h-5 text-[#00FF66]" />
              <span
                className="text-xs uppercase tracking-[0.2em] text-[#00FF66]"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                Your Profile
              </span>
            </div>
            <h1
              style={{ fontFamily: "'Bodoni Moda', serif" }}
              className="text-4xl sm:text-5xl tracking-tighter text-[#FAFAFA]"
            >
              {user?.name}
            </h1>
          </div>
          <Button
            data-testid="edit-profile-btn"
            onClick={() => (editing ? handleSave() : setEditing(true))}
            disabled={saving}
            className={
              editing
                ? "bg-[#00FF66] text-black font-semibold rounded-none border border-[#00FF66] hover:bg-[#33FF85] h-10 px-5"
                : "bg-transparent text-[#FAFAFA] border border-white/20 rounded-none hover:bg-white/5 h-10 px-5"
            }
          >
            {editing ? (
              <>
                <Save size={14} className="mr-2" />
                {saving ? "Saving..." : "Save"}
              </>
            ) : (
              <>
                <Edit3 size={14} className="mr-2" />
                Edit
              </>
            )}
          </Button>
        </div>

        {/* Verification Badge */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-2 h-2 rounded-full bg-[#00FF66] pulse-badge" />
          <ShieldCheck className="w-4 h-4 text-[#00FF66]" />
          <span
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
            className="text-[#00FF66] text-xs tracking-widest uppercase"
          >
            FBI Verified
          </span>
        </div>

        {/* Anonymous Mode Toggle */}
        <div
          className="mb-8 bg-[#0C0D10] border border-[#1F2027] p-5 flex items-center justify-between gap-4"
          data-testid="anonymous-mode-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-md bg-[#7C3AED]/15 border border-[#7C3AED]/30 flex items-center justify-center shrink-0">
              <UserX size={16} className="text-[#B084FF]" />
            </div>
            <div>
              <p
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                className="text-[#FAFAFA] text-sm font-semibold"
              >
                Anonymous Mode
              </p>
              <p className="text-[#8A8A93] text-xs mt-1 max-w-md">
                Hide your name and photos in discovery. Your identity is revealed
                only after a mutual match.
              </p>
            </div>
          </div>
          <Switch
            data-testid="anonymous-mode-switch"
            checked={anonymousMode}
            onCheckedChange={(v) => {
              setAnonymousMode(v);
              if (!editing) setEditing(true);
            }}
          />
        </div>

        {/* Photos */}
        <div className="mb-8">
          <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-1 block">
            Photos
          </Label>
          <p className="text-[#FF3B6B] text-xs mb-3 font-medium">
            Photos must be real and no older than 30 days.
          </p>
          <div className="flex flex-wrap gap-3">
            {photos.map((p, i) => (
              <div
                key={i}
                className="relative w-24 h-24 border border-[#1F2027] overflow-hidden group"
              >
                <img src={p} alt="" className="w-full h-full object-cover" />
                {editing && (
                  <button
                    onClick={() =>
                      setPhotos(photos.filter((_, idx) => idx !== i))
                    }
                    className="absolute top-1 right-1 w-5 h-5 bg-[#FF2E2E] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} className="text-white" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {editing && photos.length < 6 && (
            <div className="flex gap-2 mt-3">
              <Input
                data-testid="profile-photo-url-input"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
                placeholder="Paste photo URL"
                className="bg-[#0C0D10] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] h-10 text-sm flex-1"
              />
              <Button
                type="button"
                onClick={addPhoto}
                className="bg-transparent border border-[#1F2027] text-[#FAFAFA] rounded-none hover:bg-white/5 h-10 px-4"
              >
                <Plus size={16} />
              </Button>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="bg-[#0C0D10] border border-[#1F2027] p-4">
            <span className="text-[#8A8A93] text-xs uppercase tracking-widest block mb-1">
              Age
            </span>
            <span className="text-[#FAFAFA] text-lg">{user?.age}</span>
          </div>
          <div className="bg-[#0C0D10] border border-[#1F2027] p-4">
            <span className="text-[#8A8A93] text-xs uppercase tracking-widest block mb-1">
              Gender
            </span>
            <span className="text-[#FAFAFA] text-lg capitalize">
              {user?.gender}
            </span>
          </div>
        </div>

        {/* Location */}
        <div className="mb-8">
          <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
            Location
          </Label>
          {editing ? (
            <Input
              data-testid="profile-location-input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="bg-[#0C0D10] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] h-11"
            />
          ) : (
            <div className="flex items-center gap-2 text-[#FAFAFA]">
              <MapPin size={16} className="text-[#8A8A93]" />
              <span>{user?.location || "Not set"}</span>
            </div>
          )}
        </div>

        {/* Bio */}
        <div className="mb-8">
          <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-2 block">
            Bio
          </Label>
          {editing ? (
            <Textarea
              data-testid="profile-bio-input"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              className="bg-[#0C0D10] border-[#1F2027] text-[#FAFAFA] rounded-none focus:ring-1 focus:ring-[#00FF66] focus:border-[#00FF66] min-h-[100px]"
            />
          ) : (
            <p className="text-[#FAFAFA]/80 leading-relaxed">
              {user?.bio || "No bio yet"}
            </p>
          )}
        </div>

        {/* Interests */}
        <div>
          <Label className="text-[#8A8A93] text-xs uppercase tracking-widest mb-3 block">
            Interests
          </Label>
          {editing ? (
            <div className="flex flex-wrap gap-2">
              {INTEREST_OPTIONS.map((interest) => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => toggleInterest(interest)}
                  className={`px-3 py-1.5 text-sm border transition-colors ${
                    interests.includes(interest)
                      ? "bg-[#00FF66]/10 border-[#00FF66]/50 text-[#00FF66]"
                      : "bg-transparent border-[#1F2027] text-[#8A8A93] hover:border-[#8A8A93]"
                  }`}
                >
                  {interest}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(user?.interests || []).map((interest) => (
                <span
                  key={interest}
                  className="px-3 py-1.5 text-sm bg-[#00FF66]/10 border border-[#00FF66]/50 text-[#00FF66]"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  {interest}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
